import { pxToRem } from '@deltek/specification-client/core/Utilities/unitUtils'
import Button from '@material-ui/core/Button'
import Dialog from '@material-ui/core/Dialog'
import DialogActions from '@material-ui/core/DialogActions'
import DialogTitle from '@material-ui/core/DialogTitle'
import { makeStyles, Theme } from '@material-ui/core/styles'
import TextField from '@material-ui/core/TextField'
import Typography from '@material-ui/core/Typography'
import Autocomplete, {
    AutocompleteChangeReason,
    AutocompleteInputChangeReason,
} from '@material-ui/lab/Autocomplete'
import { ListingCardAutosuggest } from 'api/graphQL/types/product.type'
import React from 'react'

const useStyles = makeStyles((theme: Theme) => ({
    dialogTitle: {
        '& .MuiTypography-h6': {
            color: '#304959',
            fontSize: pxToRem(34),
        },
        '&.MuiDialogTitle-root': {
            paddingLeft: theme.spacing(0),
            paddingRight: theme.spacing(0),
        },
    },
    actionButton: {
        color: '#A78966',
        fontSize: pxToRem(18),
        width: 108,
        marginBottom: 16,
    },
    promptText: {
        paddingTop: theme.spacing(1),
        fontSize: pxToRem(14),
        color: theme.palette.common.black,
    },
    textField: {
        width: pxToRem(450),
    },
    dialog: {
        '& .MuiDialog-paperScrollPaper': {
            paddingLeft: theme.spacing(3),
            paddingRight: theme.spacing(3),
        },
    },
    dialogAction: {
        '&.MuiDialogActions-root': {
            paddingLeft: theme.spacing(0),
            paddingRight: theme.spacing(0),
        },
    },
}))

export interface AddProductTypeDialogProps {
    /**
     * Determines whether dialog is open.
     */
    openDialog: boolean

    /**
     * Callback when dialog is cancelled
     */
    handleCancel?: VoidFunction

    /**
     * Callback when dialog is OK'ed
     */
    handleOK?: (
        value: Partial<ListingCardAutosuggest>,
        searchedText: string
    ) => void

    /**
     * Text for the dialog title.
     */
    dialogHeading?: string

    /**
     * Autocomplete options.
     */
    options: Partial<ListingCardAutosuggest>[]

    /**
     * loading options.
     */
    loadingOptions?: boolean

    /*
     * Defines the prompt placed above the input textbox.
     */
    inputPrompt?: string

    /*
     * Defines the hint/label for the input textbox.
     */
    dropDownLabel?: string

    /*
     * Function for searching input text.
     */
    handleSearchInput?: (value: string) => void

    /*
     * Defines the text for cancel action button on confirmation dialog. Default: Cancel
     */
    cancelText?: string

    /*
     * Defines the text for confirmation action button  on confirmation dialog. Default: OK
     */
    okText?: string
}
const AddProductTypeDialog: React.FC<AddProductTypeDialogProps> = ({
    openDialog,
    handleCancel,
    handleOK,
    dialogHeading,
    options,
    loadingOptions,
    inputPrompt,
    dropDownLabel,
    handleSearchInput,
    cancelText = 'Cancel',
    okText = 'OK',
}) => {
    const classes = useStyles()

    const [searchedText, setsearchedText] = React.useState<string>('')

    const [
        selectedProductType,
        setSelectedProductType,
    ] = React.useState<Partial<ListingCardAutosuggest> | null>(null)

    const handleOKClick = React.useCallback(() => {
        if (selectedProductType || searchedText) {
            handleOK?.(selectedProductType!, searchedText)
        }
    }, [handleOK, selectedProductType, searchedText])

    const handleCancelClick = React.useCallback(() => {
        handleCancel?.()
    }, [handleCancel])

    // New Code
    const handleInputChange = (
        _event: React.ChangeEvent<{}>,
        value: string,
        reason: AutocompleteInputChangeReason
    ) => {
        if (reason !== 'reset') {
            handleSearchInput?.(value)
            setsearchedText(value)
        }
    }
    const handleOpen = () => {
        handleSearchInput?.(searchedText)
    }

    const handleChange = (
        _event: React.ChangeEvent<{}>,
        value: Partial<ListingCardAutosuggest> | null,
        reason: AutocompleteChangeReason
    ) => {
        setSelectedProductType(reason !== 'clear' ? value : null)
        setsearchedText('')
    }

    return (
        <Dialog
            open={openDialog}
            data-testid="AddProductTypeDialog"
            className={classes.dialog}
        >
            <DialogTitle className={classes.dialogTitle}>
                {dialogHeading}
            </DialogTitle>
            <Typography className={classes.promptText}>
                {inputPrompt}
            </Typography>
            <Autocomplete
                data-testid="AddProductTypeAutocomplete"
                options={options}
                clearOnBlur={false}
                ListboxProps={{ 'data-testid': 'options' }}
                getOptionLabel={(option) =>
                    `${option.parentProductFamily?.split(' ')?.[0]} - ${
                        option.productType
                    } (PTID: ${option.specAgentProductTypeId})`
                }
                className={classes.textField}
                onInputChange={handleInputChange}
                onChange={handleChange}
                loading={loadingOptions}
                onOpen={handleOpen}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        label={dropDownLabel}
                        variant="outlined"
                        data-testid="AddProductTypeTextField"
                    />
                )}
            />

            <DialogActions className={classes.dialogAction}>
                <Button
                    data-testid="AddProductTypeDialogCancelButton"
                    className={classes.actionButton}
                    onClick={handleCancelClick}
                >
                    {cancelText}
                </Button>
                <Button
                    data-testid="AddProductTypeDialogOKButton"
                    onClick={handleOKClick}
                    className={classes.actionButton}
                    disabled={
                        selectedProductType == null && searchedText === ''
                    }
                >
                    {okText}
                </Button>
            </DialogActions>
        </Dialog>
    )
}

export default AddProductTypeDialog
